import { DialogActions, Grid } from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useErrorHandling } from '@/utils/errorHandling';
import {
    PlandayCompanyChildren,
    PlandayCompanyResponse
} from '@/models/payment';
import {
    PrimaryButtonStyle,
    SecondaryButtonStyle
} from '@/pages/company/common/IntegrationDialog';
import { PrimaryInvertedButton } from '@/components/shared/Button';
import { FONT_WEIGHT, Title } from '@/typography/Typography';
import { RadioButton } from '@/components/shared/RadioButton';
import { useAppDispatch } from '@/lib/hooks';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import {
    StyledDialog,
    StyledDialogContainer
} from '@/components/consent/StyledDialog';
import { PlandayIcon } from '@/icons/PlandayIcon';
import { ConnectHeader } from '@/pages/company/common/ConnectHeader';
import styled from '@emotion/styled';
import { useCurrentCompanyDetails } from '@/services/paymentsQuery';

const ListContainer = styled('div')`
    overflow-y: auto;
    max-height: 300px;
    border: 1px solid #eaecf0;
`;

const ListHeader = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 10px 20px;
    border-radius: 5px;
    border-bottom: 1px solid #eaecf0;
    margin-bottom: 10px;
`;

export const ListItem = styled('li')`
    display: flex;
    justify-content: space-between;
    padding: 2px 15px;
    border-bottom: 1px solid #eaecf0;
    align-items: center;
`;

interface PlandayConnectProps {
    onClose?: () => void;
    onSaveSuccess?: () => void;
    onReconnect?: () => void;
    companyInfo?: PlandayCompanyResponse;
    show: boolean;
    clientId?: string;
    refreshToken?: string;
}

export const PlandayCompanySelect = ({
    onSaveSuccess,
    onClose,
    companyInfo,
    onReconnect,
    show,
    clientId,
    refreshToken
}: PlandayConnectProps) => {
    const { t } = useTranslation();

    const { data: companyDetails } = useCurrentCompanyDetails();

    const [selectedChild, setSelectedChild] = useState<
        PlandayCompanyChildren | undefined
    >(undefined);

    useEffect(() => {
        let selected = companyInfo?.children?.find(
            (child) => child.id === companyInfo?.portalId
        );
        setSelectedChild(selected);
    }, [companyInfo]);

    const { setAlert } = useNotificationsSnackbar();
    const { backendErrorHandler } = useErrorHandling();

    const dispatch = useAppDispatch();

    const { updatePlandayChildCompany } = usePaymentsMutations();

    useEffect(() => {
        if (updatePlandayChildCompany.isSuccess) {
            setAlert('Connected successfully', 'success');
            onSaveSuccess?.();
        }
    }, [updatePlandayChildCompany.isSuccess]);

    useEffect(() => {
        dispatch(setLoader(updatePlandayChildCompany.isPending));
        if (updatePlandayChildCompany.isError) {
            backendErrorHandler('Could not connect with Planday');
        }
    }, [updatePlandayChildCompany]);

    const onClick = () => {
        updatePlandayChildCompany.mutate({
            portalId: selectedChild?.id!,
            clientId: clientId!,
            refreshToken: refreshToken!
        });
    };

    return (
        <>
            <StyledDialog maxWidth="xl" open={show}>
                <StyledDialogContainer style={{ width: '664px' }}>
                    <ConnectHeader
                        logo={<PlandayIcon />}
                        title={'You are connected to Planday'}
                        id={companyDetails?.data?.name}
                    />
                    <ListContainer>
                        <ListHeader style={{ justifyContent: 'center' }}>
                            <Title fontWeight={FONT_WEIGHT.BOLD}>
                                {t('choose_entity', {
                                    type: companyInfo?.plandayTokenType
                                })}
                            </Title>
                        </ListHeader>
                        {companyInfo &&
                            companyInfo?.children.map((data, _index) => (
                                <ListItem
                                    key={1}
                                    onClick={() => setSelectedChild(data)}
                                >
                                    <Grid item xs={11.5} display={'flex'}>
                                        <Grid
                                            item
                                            xs={2.5}
                                            alignItems={'center'}
                                            display={'flex'}
                                        >
                                            {data?.id}
                                        </Grid>
                                        <Grid
                                            item
                                            xs={5}
                                            display={'block'}
                                            margin={'auto'}
                                        >
                                            <Title>{data?.name}</Title>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={5.5}
                                            display={'block'}
                                            margin={'auto'}
                                        >
                                            {data?.aliases.map((alias) => {
                                                return (
                                                    <Title key={alias}>
                                                        {alias}
                                                    </Title>
                                                );
                                            })}
                                        </Grid>
                                    </Grid>
                                    <Grid
                                        alignItems={'center'}
                                        item
                                        xs={1}
                                        justifyItems={'flex-end'}
                                    >
                                        <RadioButton
                                            checked={
                                                data?.id === selectedChild?.id
                                            }
                                        />
                                    </Grid>
                                </ListItem>
                            ))}
                    </ListContainer>
                </StyledDialogContainer>

                <DialogActions
                    sx={{
                        justifyContent: 'space-between',
                        marginX: 2,
                        marginBottom: 2
                    }}
                >
                    <SecondaryButtonStyle
                        style={{ width: '197px' }}
                        onClick={onClose}
                    >
                        {t('cancel')}
                    </SecondaryButtonStyle>

                    <PrimaryInvertedButton
                        style={{ width: '197px' }}
                        onClick={onReconnect}
                        size="medium"
                    >
                        {t('reconnect')}
                    </PrimaryInvertedButton>

                    <PrimaryButtonStyle
                        style={{ width: '197px' }}
                        onClick={onClick}
                        size="medium"
                        disabled={
                            selectedChild?.id === undefined ||
                            !clientId ||
                            !refreshToken
                        }
                    >
                        {t('save_settings')}
                    </PrimaryButtonStyle>
                </DialogActions>
            </StyledDialog>
        </>
    );
};
